import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';
import { cloneDeep, find, findIndex, get } from 'lodash';

import {
  IComponentStateHistory,
  IValueChangedEvent,
  IWizardContentComponent
} from '../../../wizard.content';
import { REMOVED } from '../../../services/base.wizard.step';
import { DatasourceWizardService } from '../../../services/datasource/datasource.wizard.service';

const DEVICE_NAME = 'name';
const HOST_NAME = 'hostname';
const USER_NAME = 'username';
const PWD = 'password';

@Component({
  selector: 'datasource-definition-html',
  templateUrl: './datasource.definition.page.html',
  providers: [],
  styleUrls: ['../datasource.style.scss']
})
export class DatasourceDefinitionComponent implements IWizardContentComponent, OnInit, OnDestroy {
  @Input() step: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() subStep: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  @Input() previousOutput: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>(); // eslint-disable-line @angular-eslint/no-output-on-prefix

  dataSourceType: string;
  params: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  hide = true;
  dataSourceForm: UntypedFormGroup;
  formChanges: Subscription;
  placeHolderText = 'Enter password';
  passwordMandatory = true;

  constructor(private wizardService: DatasourceWizardService, private fb: UntypedFormBuilder) {}

  get deviceNameCtrl(): AbstractControl {
    return this.dataSourceForm.get(DEVICE_NAME);
  }

  get hostNameCtrl(): AbstractControl {
    return this.dataSourceForm.get(HOST_NAME);
  }

  get userNameCtrl(): AbstractControl {
    return this.dataSourceForm.get(USER_NAME);
  }

  get passwordCtrl(): AbstractControl {
    return this.dataSourceForm.get(PWD);
  }

  ngOnInit() {
    this.dataSourceType = this.previousOutput.datasourceType.id;

    // temporarily disabling update of host/ip for gigastor and gigaflow devices (see OLPTC-4155)
    // checking only for existing items, to allow IP definition for new items
    let noIpEdit = false;
    if (this.wizardService.editMode) {
      noIpEdit = true;
      this.placeHolderText = '*****';
    }

    this.dataSourceForm = this.fb.group({});
    this.dataSourceForm.addControl(
      DEVICE_NAME,
      new UntypedFormControl('', [Validators.required, Validators.maxLength(30)])
    );
    this.dataSourceForm.addControl(
      HOST_NAME,
      new UntypedFormControl({ value: '', disabled: noIpEdit }, [Validators.required])
    );
    this.dataSourceForm.addControl(
      USER_NAME,
      new UntypedFormControl('', [Validators.required, Validators.maxLength(30)])
    );
    this.dataSourceForm.addControl(
      PWD,
      new UntypedFormControl('', [Validators.required, Validators.maxLength(75)])
    );

    //PWD should be optional for gigastor and gigaflow while editing (see OLPTC-4559)
    if (this.wizardService.editMode) {
      this.dataSourceForm.controls[PWD].clearValidators();
      this.dataSourceForm.controls[PWD].setValidators([Validators.maxLength(75)]);
      this.passwordMandatory = false;
    }

    this.step.form = this.dataSourceForm;
    this.params = [];
    this.resetOutput();
    this.registerFormChangesListeners();
  }

  ngOnDestroy() {
    if (this.formChanges) {
      this.formChanges.unsubscribe();
    }
  }

  registerFormChangesListeners(): void {
    this.formChanges = this.dataSourceForm.valueChanges.subscribe(newValue => {
      const updatedFields = Object.keys(newValue);
      if (updatedFields.includes(DEVICE_NAME)) {
        this.nameUpdated();
      }
      if (updatedFields.includes(USER_NAME)) {
        this.updateParamValue('username', newValue[USER_NAME]);
      }
      if (updatedFields.includes(PWD)) {
        this.updatePasswordParam(newValue[PWD]);
      }

      this.emitParamValue();
    });
  }

  resetOutput(): void {
    if (!this.wizardService.editMode) {
      this.restoreName();
      this.dataSourceForm.get(HOST_NAME).setValue(this.getHostName(this.restoreParam('host')));
      this.dataSourceForm.get(USER_NAME).setValue(this.restoreParam('username'));
      this.dataSourceForm.get(PWD).setValue(this.restoreParam('password'));
    } else {
      this.dataSourceForm.get(DEVICE_NAME).setValue(get(this.previousOutput, 'name'));
      this.dataSourceForm
        .get(HOST_NAME)
        .setValue(this.getHostName(this.restoreParams(this.previousOutput.params, 'host')));
      this.dataSourceForm
        .get(USER_NAME)
        .setValue(this.restoreParams(this.previousOutput.params, 'username'));
      this.dataSourceForm
        .get(PWD)
        .setValue(this.restoreParams(this.previousOutput.params, 'password'));

      // NOTE: the array with 'params' may have many more entries than just those 3 from above.
      // even if we are not aware of them, we should not discard them. Hence make sure all are properly copied
      this.params = cloneDeep(this.previousOutput.params);
      if (!this.params) {
        this.params = [];
      }
    }
    this.emitParamValue();
  }

  nameUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'nameInput',
      key: 'name',
      newValue: this.dataSourceForm.get(DEVICE_NAME).value,
      isValid: true
    });
  }

  emitParamValue(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'paramsInput',
      key: 'params',
      newValue: this.params,
      isValid: true
    });
  }

  restoreName(): void {
    const name = this.stateHistory(this.componentId, 'nameInput');
    if (name?.value && name.value !== REMOVED) {
      this.dataSourceForm.get(DEVICE_NAME).setValue(name.value);
    }
  }

  getHostName(hostName: string): string {
    let host = hostName;
    if (hostName.indexOf(':') !== -1) {
      const hostPortValue = hostName.split(':');
      host = hostPortValue[0];
    }
    return host;
  }

  restoreParam(key: string): string {
    const paramsInput = this.stateHistory(this.componentId, 'paramsInput');

    if (paramsInput) {
      return this.restoreParams(paramsInput.value, key);
    }

    return '';
  }

  // eslint-disable-next-line
  restoreParams(paramsInput: any, key: string): string {
    const param = find(paramsInput, obj => obj.name === key);
    return param?.value ? param.value : '';
  }

  updateParamValue(key: string, value: string): void {
    const index = findIndex(this.params, { name: key });
    const newObj = {
      name: key,
      value: value.trim()
    };
    if (index !== -1) {
      this.params.splice(index, 1, newObj);
    } else {
      this.params.push(newObj);
    }
  }

  updatePasswordParam(value: string): void {
    if (this.wizardService.editMode && !value) {
      const index = findIndex(this.params, { name: 'password' });
      if (index !== -1) {
        this.params.splice(index, 1);
      }
    } else {
      this.updateParamValue('password', value);
    }
  }
}
